import * as Yup from 'yup';
import { PARTIAL_SPECIAL_OCCASION_TYPES } from '../store';
import {
  FrequencyEnum,
  SpecialOccasionTypeEnum,
  TaxTreatmentEnum
} from '@pxtra-client-lib-legacy/gen';
import Util from '../../../../misc/util';

export const TITLE_MAX_LENGTH = 50;
export const DESCRIPTION_MAX_LENGTH = 800;

export const TAX_TREATMENT_MAX_AMOUNT: Partial<Record<TaxTreatmentEnum, number>> = {
  SPECIAL_OCCASION: 6000,
  NON_CASH_GIFT: 1000000,
  NON_CASH_BENEFIT: 5000,
  RELIEF_PREMIUM_2026: 100000
};

export const descriptionSchema = (t: (key: string) => string) =>
  Yup.object().shape({
    title: Yup.string()
      .required(t('bonus:error:titleMissing'))
      .max(TITLE_MAX_LENGTH, ({ value }) => `${value?.length ?? 0} / ${TITLE_MAX_LENGTH}`),
    description: Yup.string()
      .optional()
      .max(
        DESCRIPTION_MAX_LENGTH,
        ({ value }) => `${value?.length ?? 0} / ${DESCRIPTION_MAX_LENGTH}`
      )
  });

export const taxTreatmentSchema = () =>
  Yup.object().shape({
    taxTreatment: Yup.string().required(),
    specialOccasionType: Yup.string()
      .nullable()
      .when('taxTreatment', {
        is: 'SPECIAL_OCCASION',
        then: (schema) => schema.required()
      })
  });

export const payoutTypeSchema = (
  t: (key: string, options?: unknown) => string,
  editingNotAllowed: boolean = false
) =>
  Yup.object().shape({
    taxTreatment: Yup.string().required(),
    payoutType: Yup.string().required(),
    amount: Yup.number()
      .typeError(t('common:missingAmount'))
      .required(t('common:missingAmount'))
      .test('voucher-amount', t('bonus:error:roundAmount'), function (value) {
        const { payoutType } = this.parent;
        if (payoutType === 'APP_VOUCHER' && value) {
          return value % 5 === 0;
        }
        return true;
      })
      .test('max-amount', '', function (value) {
        const { taxTreatment } = this.parent;
        const maxAmount =
          taxTreatment && TAX_TREATMENT_MAX_AMOUNT[taxTreatment as TaxTreatmentEnum];

        if (taxTreatment && maxAmount && value && value * 100 > maxAmount) {
          return this.createError({
            message: t('bonus:error:maxAmount', {
              amount: Util.formatCurrency(maxAmount)
            })
          });
        }
        return true;
      }),
    specialOccasionType: Yup.string()
      .nullable()
      .when('taxTreatment', {
        is: 'SPECIAL_OCCASION',
        then: (schema) => schema.required(t('bonus:error:occasionMissing'))
      }),
    payoutMonth: Yup.date()
      .nullable()
      .when(['taxTreatment', 'specialOccasionType', 'frequency'], {
        is: (
          taxTreatment: TaxTreatmentEnum,
          specialOccasionType: SpecialOccasionTypeEnum,
          frequency: FrequencyEnum
        ) => {
          if (taxTreatment === 'SPECIAL_OCCASION') {
            return PARTIAL_SPECIAL_OCCASION_TYPES.includes(specialOccasionType);
          }
          if (
            ['NON_CASH_GIFT', 'NON_CASH_BENEFIT', 'FULLY_TAXED', 'RELIEF_PREMIUM_2026'].includes(
              taxTreatment
            )
          ) {
            return frequency === 'ONE_TIME';
          }
          return false;
        },
        then: (schema) => schema.required(t('bonus:error:payoutMonthMissing'))
      })
      .test('future-date', t('bonus:error:date'), function (value) {
        if (editingNotAllowed) {
          return true;
        }
        const { specialOccasionType, taxTreatment } = this.parent;
        if (
          taxTreatment === 'SPECIAL_OCCASION' &&
          PARTIAL_SPECIAL_OCCASION_TYPES.includes(specialOccasionType)
        ) {
          return true;
        }
        if (value) {
          const today = new Date();
          const currentMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
          return value >= currentMonthStart;
        }
        return true;
      })
      .test('not-current-month-on-last-day', t('bonus:error:payoutMonthLastDay'), function (value) {
        if (editingNotAllowed || !value) {
          return true;
        }
        const today = new Date();
        const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
        if (today.getDate() !== lastDayOfMonth) {
          return true;
        }
        return !(
          value.getFullYear() === today.getFullYear() && value.getMonth() === today.getMonth()
        );
      })
      .test('relief-premium-deadline', t('bonus:error:reliefPremiumDeadline'), function (value) {
        const { taxTreatment } = this.parent;
        if (taxTreatment === 'RELIEF_PREMIUM_2026' && value) {
          const maxDeadline = new Date(2027, 5, 30);
          return value <= maxDeadline;
        }
        return true;
      }),
    frequency: Yup.string()
      .nullable()
      .when('taxTreatment', {
        is: (taxTreatment: TaxTreatmentEnum) =>
          taxTreatment === 'SPECIAL_OCCASION' ||
          ['NON_CASH_GIFT', 'NON_CASH_BENEFIT', 'FULLY_TAXED', 'RELIEF_PREMIUM_2026'].includes(
            taxTreatment
          ),
        then: (schema) =>
          schema.test('frequency-required', t('bonus:error:frequencyMissing'), function (value) {
            const { specialOccasionType, taxTreatment } = this.parent;
            if (taxTreatment === 'SPECIAL_OCCASION') {
              if (!PARTIAL_SPECIAL_OCCASION_TYPES.includes(specialOccasionType)) {
                return !!value;
              }
            } else {
              return !!value;
            }
            return true;
          })
      }),
    startAccountingPeriod: Yup.date()
      .nullable()
      .when(['taxTreatment', 'specialOccasionType', 'frequency'], {
        is: (
          taxTreatment: TaxTreatmentEnum,
          specialOccasionType: SpecialOccasionTypeEnum,
          frequency: FrequencyEnum
        ) =>
          (['BIRTHDAY', 'ANNIVERSARY'].includes(specialOccasionType) && frequency) ||
          (['NON_CASH_GIFT', 'NON_CASH_BENEFIT', 'FULLY_TAXED', 'RELIEF_PREMIUM_2026'].includes(
            taxTreatment
          ) &&
            frequency === 'RECURRING'),
        then: (schema) => schema.required(t('bonus:error:startDateMissing'))
      })
      .test('future-date', t('bonus:error:date'), function (value) {
        if (editingNotAllowed) {
          return true;
        }
        if (value) {
          const currentMonthStart = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
          return value >= currentMonthStart;
        }
        return true;
      })
      .test('not-current-month-on-last-day', t('bonus:error:payoutMonthLastDay'), function (value) {
        if (editingNotAllowed || !value) {
          return true;
        }
        const today = new Date();
        const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
        if (today.getDate() !== lastDayOfMonth) {
          return true;
        }
        return !(
          value.getFullYear() === today.getFullYear() && value.getMonth() === today.getMonth()
        );
      }),
    endAccountingPeriod: Yup.date()
      .nullable()
      .test('end-date-after-start', t('bonus:error:endDate'), function (value) {
        const { startAccountingPeriod } = this.parent;
        if (value && startAccountingPeriod) {
          return value > startAccountingPeriod;
        }
        return true;
      }),
    interval: Yup.array()
      .nullable()
      .when(['taxTreatment', 'frequency'], {
        is: (taxTreatment: TaxTreatmentEnum, frequency: FrequencyEnum) =>
          ['NON_CASH_GIFT', 'NON_CASH_BENEFIT', 'FULLY_TAXED', 'RELIEF_PREMIUM_2026'].includes(
            taxTreatment
          ) && frequency === 'RECURRING',
        then: (schema) => schema.required(t('bonus:error:intervalMissing'))
      }),
    anniversaryInterval: Yup.number()
      .nullable()
      .when(['specialOccasionType', 'frequency'], {
        is: (specialOccasionType: SpecialOccasionTypeEnum, frequency: FrequencyEnum) =>
          specialOccasionType === 'ANNIVERSARY' && frequency === 'ONE_TIME',
        then: (schema) => schema.required(t('bonus:error:anniversaryMissing'))
      })
      .when(['specialOccasionType', 'frequency'], {
        is: (specialOccasionType: SpecialOccasionTypeEnum, frequency: FrequencyEnum) =>
          specialOccasionType === 'ANNIVERSARY' && frequency === 'RECURRING',
        then: (schema) => schema.required(t('bonus:error:anniversaryIntervalMissing'))
      }),
    salaryType: Yup.string()
      .nullable()
      .when('taxTreatment', {
        is: 'FULLY_TAXED',
        then: (schema) => schema.required(t('bonus:error:salaryTypeMissing'))
      }),
    salaryTypeId: Yup.string()
      .nullable()
      .when('taxTreatment', {
        is: 'FULLY_TAXED',
        then: (schema) => schema.required(t('bonus:error:salaryTypeIdMissing'))
      })
  });

export const employeeAssignmentSchema = (t: (key: string) => string) =>
  Yup.object().shape({
    employees: Yup.array().min(1, t('common:error:selectAtLeastOne'))
  });

export const messageSchema = (t: (key: string) => string) =>
  Yup.object().shape({
    message: Yup.string().required(t('bonus:missingMessage'))
  });
